import { Component, HostListener, inject } from '@angular/core';
import { ConnectionPositionPair } from '@angular/cdk/overlay';
import moment from 'moment';
import { SubsectionPreviewComponent } from '../../../../project-subsection/preview/subsection-preview.component';
import { BaseTreeNode, TreeNodeType, TreeState } from '../../../../project-subsection/models/tree-node';
import { TreeManagerService } from '../../../../project-subsection/services/tree-manager.service';
import { SubsectionService } from '../../../services/subsection.service';
import { TopMediaInfoService } from '../../top-media-info/top-media-info.service';
import { UserVote } from '../../../interfaces';
import { SimpleUser } from '../../../interfaces/user.interface';
import { ProjectService } from '../../../services/project.service';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'viewer-reject-button',
  templateUrl: './viewer-reject-button.component.html',
  styleUrl: './viewer-reject-button.component.scss',
  standalone: false,
})
export class ViewerRejectButtonComponent {
  private readonly topMediaInfoService: TopMediaInfoService = inject(TopMediaInfoService);
  private readonly subsectionService: SubsectionService = inject(SubsectionService);
  private readonly treeManagerService: TreeManagerService = inject(TreeManagerService);
  private readonly projectService: ProjectService = inject(ProjectService);
  private readonly authService: AuthService = inject(AuthService);

  readonly treeNodeType: typeof TreeNodeType = TreeNodeType;

  readonly votePopupPositions: ConnectionPositionPair[] = [
    {
      originX: 'center',
      originY: 'top',
      overlayX: 'center',
      overlayY: 'bottom',
      offsetY: -10,
    },
  ];

  popupOpen: boolean = false;

  get subSectionPreview(): SubsectionPreviewComponent | null {
    return this.topMediaInfoService.getSubsectionPreview();
  }

  get treeState(): TreeState<BaseTreeNode> | null {
    return this.treeManagerService.getCurrentState() ?? null;
  }

  get isShareLinkPreview(): boolean {
    return !!this.subsectionService.uiState?.isShareLinkPreview;
  }

  get isReleasePreview(): boolean {
    return !!this.subsectionService.uiState?.isReleasePreview;
  }

  get shouldDisableInteractiveElements(): boolean {
    return !!this.subSectionPreview?.hasLockedVersionInCurrentOption || this.isReleasePreview;
  }

  get showVotingUi(): boolean {
    const sp: SubsectionPreviewComponent | null = this.subSectionPreview;
    return (
      !sp?.subSectionState?.reviewLink &&
      sp?.versionData != null &&
      !!sp?.canVote &&
      this.treeState?.selectedNode?.type === TreeNodeType.File &&
      !this.isShareLinkPreview &&
      !this.isReleasePreview &&
      !this.shouldDisableInteractiveElements
    );
  }

  get enableVoting(): boolean {
    const username: string | null = this.authService.getAuthUserName();
    return !!username && (this.projectService.project?.validators?.includes(username) ?? false);
  }

  get currentUsername(): string {
    return this.subSectionPreview?.currentUsername ?? '';
  }

  get isCurrentRejected(): boolean {
    return this.subSectionPreview?.isCurrentRejected ?? false;
  }

  get isRejectionMajority(): boolean {
    return this.totalParticipants > 0 && this.totalRejected >= this.totalParticipants * 0.5;
  }

  get allVoters(): UserVote[] {
    const proponents: UserVote[] = this.subSectionPreview?.currentProponents ?? [];
    const opponents: UserVote[] = this.subSectionPreview?.currentOpponents ?? [];
    return [...proponents, ...opponents];
  }

  get pendingParticipants(): SimpleUser[] {
    const sp: any = this.subSectionPreview;
    const participants: SimpleUser[] = sp?.projectParticipants ?? [];
    const votedUsernames: Set<string> = new Set([
      ...(this.subSectionPreview?.currentProponents ?? []).map((v: UserVote) => v.username),
      ...(this.subSectionPreview?.currentOpponents ?? []).map((v: UserVote) => v.username),
    ]);
    return participants.filter((p: SimpleUser) => !votedUsernames.has(p.username));
  }

  get totalApproved(): number {
    return this.subSectionPreview?.totalApproved ?? 0;
  }

  get totalRejected(): number {
    return this.subSectionPreview?.totalRejected ?? 0;
  }

  get totalParticipants(): number {
    const sp: any = this.subSectionPreview;
    const participants: number = sp?.projectParticipants?.length ?? 0;
    return Math.max(participants, this.totalApproved + this.totalRejected, 1);
  }

  get approvedPercent(): number {
    return Math.min(100, (this.totalApproved / this.totalParticipants) * 100);
  }

  get rejectedPercent(): number {
    return Math.min(100 - this.approvedPercent, (this.totalRejected / this.totalParticipants) * 100);
  }

  public relativeVotedAt(votedAt: number): string {
    if (!votedAt) {
      return '';
    }
    return moment(votedAt).fromNow();
  }

  public togglePopup(): void {
    this.popupOpen = !this.popupOpen;
  }

  public closeVotePopup(): void {
    this.popupOpen = false;
  }

  public rejectCurrentAsset(event: MouseEvent): void {
    event.stopPropagation();
    this.subSectionPreview?.submitRejection();
    // this.popupOpen = false;
  }

  public cancelOwnVote(event: MouseEvent): void {
    event.stopPropagation();
    this.subSectionPreview?.cancelVote();
    // this.popupOpen = false;
  }

  public openBatchApproval(event: MouseEvent): void {
    event.stopPropagation();
    this.subSectionPreview?.showApprovalModal(false);
    this.popupOpen = false;
  }

  public onPopupKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.stopPropagation();
      this.popupOpen = false;
    }
  }

  @HostListener('document:keydown.escape')
  public onEscape(): void {
    this.popupOpen = false;
  }
}
